import { locationString } from 'Location';
import { type CurrentUser } from 'Users';
import {
    type ProjectGroupMembershipSansProjectGroup,
    type ProjectGroupMemberUserAttrs,
    type MemberDetails,
    MemberDetailsMembershipStatus,
    ProjectGroupMembershipStatus,
} from './ProjectGroup.types';

export const getCreatorMemberDetails = (member: CurrentUser): MemberDetails => ({
    id: member.id,
    email: member.email!,
    avatarUrl: member.avatarUrl || null,
    name: member.name,
    jobTitle: member.careerProfile.workExperiences.find(experience => experience.featured)?.jobTitle || null,
    location: locationString(member.careerProfile.placeDetails, member.city, member.state, member.country),
    membershipStatus: ProjectGroupMembershipStatus.ACCEPTED,
    detailsStatus: MemberDetailsMembershipStatus.CREATOR,
});

export const getMemberDetailsFromMembershipUserAttrs = (member: ProjectGroupMemberUserAttrs): MemberDetails => ({
    id: member.id,
    email: member.email,
    avatarUrl: member.avatarUrl || null,
    name: member.name,
    jobTitle: member.careerProfileFeaturedWorkExperience?.jobTitle
        ? `${member.careerProfileFeaturedWorkExperience.jobTitle}, ${member.careerProfileFeaturedWorkExperience.professionalOrganization.name}`
        : null,
    location: locationString(member.careerProfilePlaceDetails, member.city, member.state, member.country),
    membershipStatus: null,
    detailsStatus: null,
});

const getDetailsStatusFromMembership = (
    membership: ProjectGroupMembershipSansProjectGroup,
): MemberDetailsMembershipStatus => {
    if (membership.isCreator) {
        return MemberDetailsMembershipStatus.CREATOR;
    }
    if (membership.status === ProjectGroupMembershipStatus.INVITED) {
        return MemberDetailsMembershipStatus.INVITED;
    }
    if (membership.status === ProjectGroupMembershipStatus.ACCEPTED) {
        return MemberDetailsMembershipStatus.ACCEPTED;
    }
    // "removed" and "declined" are treated the same since both statuses mean the user is confirmed to not be in the group.
    if ([ProjectGroupMembershipStatus.DECLINED, ProjectGroupMembershipStatus.REMOVED].includes(membership.status)) {
        return MemberDetailsMembershipStatus.DECLINED;
    }
    throw new Error(`Invalid membership status: ${membership.status}`);
};

export const getMemberDetailsFromProjectGroupMembership = (
    membership: ProjectGroupMembershipSansProjectGroup,
): MemberDetails => ({
    ...getMemberDetailsFromMembershipUserAttrs(membership.user!),
    membershipStatus: membership.status,
    detailsStatus: getDetailsStatusFromMembership(membership),
});
